import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { apiService } from '../services/api';
import type { IntegrationSettings } from '../services/integrationApi';
import {
  RELATIONSHIP_KINDS,
  SHOPIFY_RELATED_FIELDS,
  normalizeRelatedProductsSettings,
  type RelatedField,
  type RelatedProductsSettings,
  type RelationshipKind,
} from '../types/relatedProducts';
import { InlineMessage, SectionCard } from './ui';

export interface RelatedProductsSettingsSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  platform: string;
  integrationId: string;
}

const T = 'relatedProducts.settings';

/** The fields this platform's mapping may name, in table order (Shopify narrowed by the destination). */
export function fieldsForPlatform(platform: string, destination: RelatedProductsSettings['destination']): RelatedField[] {
  if (platform === 'bigcommerce') return ['bigcommerce_related'];
  return SHOPIFY_RELATED_FIELDS.filter((field) =>
    field.startsWith('motzu_') ? destination !== 'search_discovery' : destination !== 'motzu',
  );
}

/**
 * What `PUT /integrations/:id` accepts for this platform: the mapping limited to the platform's
 * own fields (the defaults name both platforms'), and no destination on BigCommerce.
 */
export function relatedSettingsPayload(platform: string, next: RelatedProductsSettings): RelatedProductsSettings {
  const own = platform === 'bigcommerce' ? new Set<RelatedField>(['bigcommerce_related']) : new Set<RelatedField>(SHOPIFY_RELATED_FIELDS);
  const mapping = {} as Record<RelationshipKind, RelatedField[]>;
  for (const kind of RELATIONSHIP_KINDS) mapping[kind] = next.mapping[kind].filter((f) => own.has(f));
  const payload = { storePicks: next.storePicks, mapping } as RelatedProductsSettings;
  if (platform === 'shopify') payload.destination = next.destination;
  return payload;
}

/**
 * "Related products" in the integration settings (flag `retailer_related_products`, MOT-705):
 * where the store's related and upsell picks and the automatic works-with lists are written, and
 * whether the picks extend or replace the automatic list. Shopify and BigCommerce only; saved with
 * the drawer's own Save.
 */
const RelatedProductsSettingsSection: React.FC<RelatedProductsSettingsSectionProps> = ({ settings, onChange, platform, integrationId }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('retailer_related_products');
  const isShopify = platform === 'shopify';
  const status = useQuery({
    queryKey: ['shopify-related-products-status', integrationId],
    queryFn: () => apiService.getShopifyRelatedProductsStatus(integrationId),
    enabled: enabled && isShopify,
    staleTime: 60_000,
  });
  if (!enabled || (platform !== 'shopify' && platform !== 'bigcommerce')) return null;

  const current = normalizeRelatedProductsSettings(settings.relatedProducts);
  const update = (next: RelatedProductsSettings) => onChange({ relatedProducts: relatedSettingsPayload(platform, next) });
  const fields = fieldsForPlatform(platform, current.destination);
  const toggle = (kind: RelationshipKind, field: RelatedField, on: boolean) => {
    const list = current.mapping[kind].filter((f) => f !== field);
    update({ ...current, mapping: { ...current.mapping, [kind]: on ? [...list, field] : list } });
  };

  const usesDiscovery = isShopify && current.destination !== 'motzu';
  const usesMotzu = isShopify && current.destination !== 'search_discovery';
  const data = status.data;
  const missingDiscovery = usesDiscovery && data?.readOk
    ? (['related_products', 'complementary_products'] as const).filter((key) => data.discovery[key] !== 'present')
    : [];
  const missingMotzu = usesMotzu && data?.readOk && !data.motzu.compatible_lenses && !data.motzu.suggested_accessories;

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense padded>
      {platform === 'bigcommerce' && <InlineMessage severity="warning">{t(`${T}.bigcommercePaused`)}</InlineMessage>}
      {isShopify && status.isError && <InlineMessage severity="info">{t(`${T}.statusError`)}</InlineMessage>}
      {missingDiscovery.map((key) => (
        <InlineMessage key={key} severity="warning">
          {t(`${T}.missingDiscovery`, { field: t(`${T}.discoveryKey.${key}`) })}
        </InlineMessage>
      ))}
      {missingMotzu && <InlineMessage severity="info">{t(`${T}.missingMotzu`)}</InlineMessage>}

      {isShopify && (
        <FormControl sx={{ mb: 2, display: 'block' }}>
          <FormLabel id={`related-destination-${integrationId}`}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{t(`${T}.destination`)}</Typography>
          </FormLabel>
          <RadioGroup
            aria-labelledby={`related-destination-${integrationId}`}
            value={current.destination}
            onChange={(e) => update({ ...current, destination: e.target.value as RelatedProductsSettings['destination'] })}
          >
            {(['motzu', 'search_discovery', 'both'] as const).map((value) => (
              <FormControlLabel key={value} value={value} control={<Radio size="small" />} label={<Typography variant="body2">{t(`${T}.destinationOption.${value}`)}</Typography>} />
            ))}
          </RadioGroup>
          {usesDiscovery && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {t(`${T}.overwriteNote`)}
            </Typography>
          )}
        </FormControl>
      )}

      <FormControl sx={{ mb: 2, display: 'block' }}>
        <FormLabel id={`related-picks-${integrationId}`}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t(`${T}.storePicks`)}</Typography>
        </FormLabel>
        <RadioGroup
          aria-labelledby={`related-picks-${integrationId}`}
          value={current.storePicks}
          onChange={(e) => update({ ...current, storePicks: e.target.value as RelatedProductsSettings['storePicks'] })}
        >
          <FormControlLabel value="extend" control={<Radio size="small" />} label={<Typography variant="body2">{t(`${T}.extend`)}</Typography>} />
          <FormControlLabel value="replace" control={<Radio size="small" />} label={<Typography variant="body2">{t(`${T}.replace`)}</Typography>} />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
          {t(`${T}.storePicksHint`)}
        </Typography>
      </FormControl>

      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{t(`${T}.mapping`)}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={t(`${T}.mapping`)}>
          <TableHead>
            <TableRow>
              <TableCell>{t(`${T}.relationship`)}</TableCell>
              {fields.map((field) => (
                <TableCell key={field} align="center">{t(`${T}.field.${field}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {RELATIONSHIP_KINDS.map((kind) => (
              <TableRow key={kind}>
                <TableCell component="th" scope="row">{t(`${T}.kind.${kind}`)}</TableCell>
                {fields.map((field) => (
                  <TableCell key={field} align="center" padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={current.mapping[kind].includes(field)}
                      onChange={(e) => toggle(kind, field, e.target.checked)}
                      slotProps={{ input: { 'aria-label': t(`${T}.cellLabel`, { kind: t(`${T}.kind.${kind}`), field: t(`${T}.field.${field}`) }) } }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
        {t(`${T}.mappingHint`)}
      </Typography>
    </SectionCard>
  );
};

export default RelatedProductsSettingsSection;
